import { asError } from './asError';

// Deferred custom tracking scripts. ScriptHelper#render_deferred_tracking_scripts
// withholds these from the server-rendered page and ships each one's raw content
// in window.popmenuDeferredScripts; this module injects them after hydration (see
// postponed.tsx) and, for consent-gated entries, once their category is approved
// (see CookieConsentBanner). Content parsed via innerHTML is inert, so each script
// element is recreated to execute.

type DeferredScriptEntry = NonNullable<Window['popmenuDeferredScripts']>[number];

const injectedEntries: Record<number, boolean> = {};

const injectEntry = (entry: DeferredScriptEntry, index: number) => {
  try {
    const target = entry.position === 'head' ? document.head : document.body;
    const container = document.createElement('div');
    container.innerHTML = entry.html;
    container.querySelectorAll('script').forEach((inert) => {
      const script = document.createElement('script');
      Array.from(inert.attributes).forEach((attr) => {
        script.setAttribute(attr.name, attr.value);
      });
      // Dynamically created scripts are async by default (execute in load order),
      // so recreated scripts whose original tag was not async get async = false.
      if (!inert.hasAttribute('async')) {
        script.async = false;
      }
      script.text = inert.text;
      inert.parentNode?.replaceChild(script, inert);
    });
    const nodes = document.createDocumentFragment();
    while (container.firstChild) {
      nodes.appendChild(container.firstChild);
    }
    if (entry.position === 'body_start') {
      target.insertBefore(nodes, target.firstChild);
    } else {
      target.appendChild(nodes);
    }
  } catch (err) {
    console.warn(`[POPMENU] Error injecting deferred script ${index}: ${asError(err).message}`);
  }
};

let deferredScriptsInjected = false;

// Injects every deferred script that does not require consent. Called once after
// hydration + first session load; later calls are no-ops.
export const injectDeferredScripts = () => {
  if (deferredScriptsInjected) return;
  deferredScriptsInjected = true;
  (window.popmenuDeferredScripts || []).forEach((entry, index) => {
    if (entry.requiresConsent) return;
    injectedEntries[index] = true;
    injectEntry(entry, index);
  });
};

// Injects consent-gated scripts whose category has been approved. Called by the
// consent banner on mount (stored consent) and whenever the visitor saves consent.
// Idempotent per entry, so overlapping category lists are safe.
export const injectConsentScripts = (approvedCategories: string[]) => {
  (window.popmenuDeferredScripts || []).forEach((entry, index) => {
    if (!entry.requiresConsent || injectedEntries[index]) return;
    if (!entry.consentCategory || !approvedCategories.includes(entry.consentCategory)) return;
    injectedEntries[index] = true;
    injectEntry(entry, index);
  });
};
